<script setup lang="ts">
  import { ref, reactive, computed, onMounted, watch, toRefs } from 'vue'
  import { toast } from 'vue-sonner'
  import { FileBarChart2, Loader2, FileText, RefreshCw } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { formatFileSize, cn } from '@/utils'

  import configService from '@/services/configService'
  import {
    AplosFileUploadService
  } from '../services/AplosFileUploadService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { AplosFile } from '../models/AplosFile'
import { isValidFileUploadType } from '../services/AplosFileUtilities'

  type Props = {
    autoUpload?: boolean
    multiple?: boolean
    nextCallback?: (e: Event) => void
    hideUploadButton?: boolean
  }

  const fileUploadService: AplosFileUploadService =
    InjectionService.injectStrict(AplosFileUploadService.injectableKey)

  const props = withDefaults(defineProps<Props>(), {
    autoUpload: false,
    multiple: false
  })

  const { autoUpload, multiple, nextCallback } = toRefs(props)

  const emit = defineEmits<{
    (e: 'updateFileRef', payload: AplosFile): void
    (e: 'upload-clicked', event?: Event): void
    (e: 'files-selected', files?: File[]): void
    (e: 'upload-complete'): void
  }>()

  const files = reactive<File[]>([])
  const dragCounter = ref(0)
  const dragging = computed(() => dragCounter.value > 0)
  const uploading = ref(false)
  const progress = ref(0)
  const maxFileSize = ref(0)
  const dragEnter = () => dragCounter.value++
  const dragLeave = () => dragCounter.value--
  const uploadingIsDone = ref(false)
  const hasUploadingError = ref(false)

  onMounted(async () => {
    maxFileSize.value = await configService.getMaxFileSize()
  })

  watch(
    files,
    (newVal) => {
      emit('files-selected', Array.from(newVal))
    },
    { immediate: true }
  )

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    dragCounter.value = 0

    const dataTransfer = e.dataTransfer
    if (!dataTransfer) return

    const uploadedFiles = Array.from(dataTransfer.files)
    if (uploadedFiles.length > 1 && !multiple.value) {
      toast.error('Only one file can be run through the Analysis Wizard at a time.')
      return
    }

    for (let file of uploadedFiles) {
      const fileExtension = `.${file.name?.split('.').pop()}`

      if (!isValidFileUploadType(file.type) && !isValidFileUploadType(fileExtension)) {
        toast.error('Invalid file type: ' + file.name)
        continue
      }

      if (file.size > maxFileSize.value) {
        toast.error(
          `File size exceeds the limit of ${formatFileSize(maxFileSize.value)}: ${file.name}`
        )
        continue
      }

      if (!multiple.value) {
        files.splice(0, files.length, file)
        break
      } else files.push(file)
    }

    if (autoUpload.value) handleUploads(e)
  }

  function handleSelect(event: Event) {
    const target = event.target as HTMLInputElement
    const uploadedFiles = Array.from(target.files ?? [])

    if (uploadedFiles.length > 1 && !multiple.value) {
      toast.error('Only one file can be run through the Analysis Wizard at a time.')
      return
    }

    for (let file of uploadedFiles) {
      const fileExtension = `.${file.name?.split('.').pop()}`

      if (!isValidFileUploadType(file.type) && !isValidFileUploadType(fileExtension)) {
        toast.error('Invalid file type: ' + file.name)
        continue
      }

      if (file.size > maxFileSize.value) {
        toast.error(
          `File size exceeds the limit of ${formatFileSize(maxFileSize.value)}: ${file.name}`
        )
        continue
      }

      if (!multiple.value) {
        files.splice(0, files.length, file)
        break
      } else files.push(file)
    }

    if (autoUpload.value) handleUploads(event)
  }

  async function handleUploads(event: Event) {
    uploadingIsDone.value = false
    uploading.value = true
    hasUploadingError.value = false
    try {
      for (const file of files) {
        await fileUploadService.handleUpload(file, uploading, progress, handleStatusUpdate, emit)
      }

      if (typeof nextCallback.value === 'function' && !hasUploadingError.value) {
        nextCallback.value(event)
      }

      if (!hasUploadingError.value) {
        emit('upload-complete')
      }
    } catch (err) {
      hasUploadingError.value = true
      throw err
    } finally {
      uploading.value = false
    }
  }

  function resetUpload() {
    uploadingIsDone.value = false
    uploading.value = false
    hasUploadingError.value = false
    files.splice(0, files.length)
    emit('files-selected', [])
  }

  function handleStatusUpdate(hasError: boolean) {
    uploadingIsDone.value = true
    hasUploadingError.value = hasError
  }

  defineExpose({ handleUploads, resetUpload })
</script>

<template>
  <div class="flex flex-col gap-5 md:flex-row">
    <div
      @dragenter.prevent="dragEnter"
      @dragleave.prevent="dragLeave"
      @drop.prevent="handleDrop"
      @dragover.prevent
      :class="
        cn(
          'flex grow justify-center rounded-lg border border-dashed px-6 py-20',
          dragging
            ? 'border-emerald-700 bg-emerald-300/30 dark:border-emerald-300 dark:bg-emerald-700/30'
            : 'border-border'
        )
      "
    >
      <div class="text-center">
        <RefreshCw
          class="mx-auto size-12 animate-spin text-gray-300 dark:text-gray-500"
          aria-hidden="true"
          v-if="uploading"
        />
        <FileBarChart2
          class="mx-auto size-12 text-gray-300 dark:text-gray-500"
          aria-hidden="true"
          v-else
        />
        <div
          :class="
            cn(
              'mt-4 justify-center text-sm leading-6 text-gray-600 dark:text-gray-300',
              uploading ? 'hidden' : 'flex'
            )
          "
        >
          <label
            for="file-upload"
            class="hover:text-primary/80 relative cursor-pointer rounded-md bg-inherit font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
          >
            <span>Upload a file</span>
            <input
              @change="handleSelect"
              id="file-upload"
              name="file-upload"
              type="file"
              class="sr-only"
            />
          </label>
          <p class="pl-1">or drag and drop</p>
        </div>
        <p
          v-if="uploading"
          class="mt-4 text-xs font-medium leading-5 text-gray-600 dark:text-gray-300"
        >
          {{ uploadingIsDone ? 'Processing file...' : 'Uploading...' }}
        </p>
        <template v-else>
          <p class="text-xs leading-5 text-gray-500 dark:text-gray-400">
            Supported filetypes: text delimited (.csv and .dat); Excel (.xls and .xlsx); and SAS
            (.sas7bdat and .xpt); parquet (.parquet)
          </p>
          <p class="text-xs leading-5 text-gray-500 dark:text-gray-400">
            Limit of {{ formatFileSize(maxFileSize) }}
          </p>
        </template>
      </div>
    </div>
    <div v-if="!autoUpload" id="files" class="flex flex-col justify-end md:basis-1/4">
      <div
        v-for="file in files"
        :key="file.name"
        class="dark:border-white/10 flex w-full grow flex-col items-center justify-center rounded-md border p-5"
      >
        <div class="flex flex-col items-center gap-3">
          <FileText class="size-10" />
          <div class="flex justify-between gap-x-5">
            <div>
              <p class="font-semibold">{{ file.name }}</p>
            </div>
            <button class="text-red-500" @click="files.splice(files.indexOf(file), 1)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <Button
        class="mt-4"
        v-if="hideUploadButton !== true"
        :disabled="uploading || files.length === 0"
        @click.prevent="
          (e: any) => {
            emit('upload-clicked', e)
            handleUploads(e)
          }
        "
      >
        <Loader2 v-if="uploading" class="mr-2 size-4 animate-spin" />
        Upload
      </Button>

      <slot name="uploadButton"></slot>
    </div>
  </div>
</template>
